import { useQueryClient } from "@tanstack/react-query";
import { RouterProvider, type RouterHistory } from "@tanstack/react-router";
import { useEffect, useState, useSyncExternalStore } from "react";

import { isApiClientError } from "../api/errors";

import {
  PwaNotices,
  PwaProvider,
  createPwaServices,
  type PwaServices,
} from "../pwa";
import { createApplicationRouter } from "./router";
import { createShellServices, type ShellServices } from "./services";

/**
 * Экраны без сессии: вход, восстановление пароля, подтверждение почты и
 * страницы ошибок. Предложение установки на них закрывало бы форму, а человеку
 * сначала нужно войти, поэтому там его не показываем.
 */
const PUBLIC_PATHS = [
  "/login",
  "/forgot-password",
  "/reset-password",
  "/email/verify",
  "/401",
  "/403",
  "/404",
  "/409",
  "/422",
  "/429",
  "/503",
];

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

export function Application({
  services,
  history,
  pwa,
}: {
  services?: ShellServices;
  history?: RouterHistory;
  pwa?: PwaServices;
}) {
  const queryClient = useQueryClient();
  const [router] = useState(() =>
    createApplicationRouter(
      queryClient,
      services ?? createShellServices(),
      history,
    ),
  );
  const [pwaServices] = useState(
    () =>
      pwa ??
      createPwaServices({
        queryClient,
        // Сессия могла истечь, пока не было связи: возвращаем ко входу и
        // помним, куда человек шёл.
        onSessionExpired: () => {
          const { pathname, searchStr, hash } = router.state.location;
          void router.navigate({
            to: "/login",
            search: { redirect: `${pathname}${searchStr}${hash}` },
          });
        },
      }),
  );

  useEffect(() => {
    const sessionError = (error: unknown) => {
      if (
        !isApiClientError(error) ||
        error.status !== 401 ||
        isPublicPath(router.state.location.pathname)
      )
        return;
      const { pathname, searchStr, hash } = router.state.location;
      // Не оставляем в кэше данные прежней сессии для следующего входа.
      queryClient.clear();
      void router.navigate({
        to: "/login",
        search: { redirect: `${pathname}${searchStr}${hash}`, expired: true },
      });
    };
    const stopQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error")
        sessionError(event.action.error);
    });
    const stopMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error")
        sessionError(event.action.error);
    });
    return () => {
      stopQueries();
      stopMutations();
    };
  }, [queryClient, router]);

  // Роутер живёт снаружи RouterProvider, поэтому на смену страницы подписываемся сами.
  const pathname = useSyncExternalStore(
    (notify) => router.subscribe("onResolved", notify),
    () => router.state.location.pathname,
  );

  return (
    <PwaProvider services={pwaServices}>
      <RouterProvider router={router} />
      <PwaNotices
        hideInstall={pathname === "/about" || isPublicPath(pathname)}
        onOpenAbout={() => void router.navigate({ to: "/about" })}
      />
    </PwaProvider>
  );
}
